import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { I18nService } from '../core/i18n.service';
import { SessionService } from '../core/session.service';
import { TournamentsApiService } from '../core/tournaments-api.service';
import { TournamentPublic } from '../core/tournaments-api.types';

@Component({
  selector: 'app-tournament-detail-page',
  standalone: true,
  imports: [CommonModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <a routerLink="/tournaments" class="back-link">{{ t('public.detail.back') }}</a>

      @if (loading()) {
        <p class="state">{{ t('public.detail.loading') }}</p>
      } @else if (error()) {
        <p class="state state--error">{{ error() }}</p>
      } @else if (tournament(); as data) {
        <article class="card hero">
          <div class="hero-head">
            <div>
              <h1>{{ data.name }}</h1>
              <p class="meta">{{ data.game }} - {{ data.format_label }} - {{ data.tournament_type }}</p>
            </div>
            <div class="hero-pills">
              <span class="pill">{{ t('public.detail.region', { value: data.region }) }}</span>
              <span class="pill">{{ data.crossplay ? t('public.detail.crossplay.on') : t('public.detail.crossplay.off') }}</span>
            </div>
          </div>
          <div class="actions">
            <a class="button button--secondary" [routerLink]="['/tournaments', data.slug, 'bracket']">{{ t('public.detail.viewBracket') }}</a>
            @if (session.isAuthenticated()) {
              <a class="button" [routerLink]="['/tournaments', data.slug, 'register']">{{ t('public.detail.registerTeam') }}</a>
            } @else if (isRegistrationOpen(data)) {
              <a class="button" routerLink="/player/login">{{ t('public.detail.goLogin') }}</a>
            }
          </div>
          @if (!session.isAuthenticated() && isRegistrationOpen(data)) {
            <p class="login-hint">{{ t('public.detail.loginNeeded') }}</p>
          }
        </article>

        <article class="card info-grid">
          <p><strong>{{ t('public.detail.start') }}</strong> {{ data.starts_at | date: 'full' }}</p>
          <p><strong>{{ t('public.detail.registrationClose') }}</strong> {{ data.registration_closes_at | date: 'full' }}</p>
          @if (data.prize_pool) {
            <p><strong>{{ t('public.detail.prize') }}</strong> {{ data.prize_pool }}</p>
          }
        </article>

        @if (data.description) {
          <article class="card section">
            <h2>{{ t('public.detail.description') }}</h2>
            <p>{{ data.description }}</p>
          </article>
        }
        @if (data.rules) {
          <article class="card section">
            <h2>{{ t('public.detail.rules') }}</h2>
            <p>{{ data.rules }}</p>
          </article>
        }
      }
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 980px; margin: 0 auto; }
    .back-link { color: var(--accent); text-decoration: none; display: inline-block; margin-bottom: 0.8rem; font-weight: 600; }
    .card { border: 1px solid var(--line-soft); border-radius: 12px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 1rem; margin-bottom: 0.8rem; box-shadow: var(--shadow-soft); }
    .hero { background: linear-gradient(180deg, rgba(31, 49, 77, 0.9) 0%, rgba(20, 31, 51, 0.92) 100%); }
    .hero-head { display: flex; justify-content: space-between; gap: 0.8rem; flex-wrap: wrap; align-items: start; }
    .hero h1 { margin: 0 0 0.35rem; font-size: 1.6rem; color: var(--text-strong); }
    h2 { margin: 0 0 0.4rem; font-size: 1.12rem; color: var(--text-strong); }
    .meta { margin: 0; color: var(--text-muted); }
    .hero-pills { display: flex; gap: 0.35rem; flex-wrap: wrap; }
    .pill { font-size: 0.8rem; color: #d4e2fb; background: rgba(84, 118, 173, 0.3); border: 1px solid rgba(104, 146, 214, 0.45); border-radius: 999px; padding: 0.2rem 0.5rem; }
    .actions { margin-top: 0.8rem; display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .login-hint { margin: 0.6rem 0 0; color: var(--text-muted); font-size: 0.92rem; }
    .button { display: inline-flex; align-items: center; border-radius: 8px; padding: 0.55rem 0.85rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; text-decoration: none; border: 1px solid rgba(125, 176, 255, 0.56); }
    .button--secondary { background: rgba(101, 145, 216, 0.18); color: #d7e7ff; border: 1px solid rgba(112, 164, 255, 0.42); }
    .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 0.55rem 0.8rem; background: linear-gradient(180deg, rgba(16, 25, 41, 0.64) 0%, rgba(14, 23, 38, 0.74) 100%); }
    .info-grid p { margin: 0; color: var(--text-muted); }
    .info-grid strong { color: #d6e7ff; }
    .section p { margin: 0; color: var(--text-muted); line-height: 1.5; }
    .state { color: var(--text-muted); }
    .state--error { color: var(--danger); }
    @media (max-width: 760px) {
      .hero-head { display: block; }
      .hero-pills { margin-top: 0.45rem; }
    }
  `
})
export class TournamentDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(TournamentsApiService);
  private readonly i18n = inject(I18nService);
  protected readonly session = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tournament = signal<TournamentPublic | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  protected isRegistrationOpen(tournament: TournamentPublic): boolean {
    const closesAt = tournament.registration_closes_at;
    if (!closesAt) {
      return false;
    }
    return new Date(closesAt).getTime() >= Date.now();
  }

  constructor() {
    this.route.paramMap
      .pipe(
        switchMap((params) => this.api.getPublicTournament(params.get('slug') ?? '')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.tournament.set(response.tournament);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(this.i18n.t('public.detail.error'));
          this.loading.set(false);
        }
      });
  }
}
